import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import BrandChip from '../components/BrandChip';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { 
  CheckCircle, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail, 
  ArrowRight,
  Building2,
  Shield,
  Zap,
  Clock
} from 'lucide-react';

const WindowCleaningRobotsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    {
      icon: Shield,
      title: "Safe at Any Height",
      description: "Anti-fall sensors and backup suction ensure your robot stays securely attached even on high-rise buildings. Safety rope included for extra protection."
    },
    {
      icon: Zap,
      title: "Powerful Suction",
      description: "3000Pa suction power keeps the robot firmly attached while cleaning. Works on all glass types including framed, frameless, and tinted windows."
    },
    {
      icon: Building2,
      title: "Commercial Grade",
      description: "Designed for office buildings, hotels, and commercial glass facades. Handles large glass panels and curtain walls efficiently."
    },
    {
      icon: Clock,
      title: "Time Saving",
      description: "What takes hours with manual cleaning takes minutes with our window robot. Programmable for automatic cleaning schedules."
    }
  ];

  const faqs = [
    {
      question: "How do window cleaning robots work?",
      answer: "Window cleaning robots use powerful suction motors to attach to glass surfaces. They navigate using AI-powered sensors, spray cleaning solution, and use rotating microfiber pads to clean the glass. The robot automatically detects window edges and plans an efficient cleaning path to cover the entire surface."
    },
    {
      question: "Are window cleaning robots safe for high-rise buildings?",
      answer: "Yes, Harpalyk window robots are equipped with multiple safety features: anti-fall sensors, backup battery that activates if power fails, dual suction motors for redundancy, and an included safety rope. They're tested for use up to 30+ floors and approved for commercial building use."
    },
    {
      question: "What types of windows can robot cleaners handle?",
      answer: "Our window robots work on framed windows, frameless glass, sliding doors, glass walls, and mirrors. They handle various glass types including clear, tinted, and low-E glass. Minimum glass thickness requirement is 3mm, and the robot can clean panels from 0.5m² to unlimited size."
    },
    {
      question: "How long does it take to clean a window?",
      answer: "A standard window (1m x 1.5m) takes approximately 3-5 minutes to clean thoroughly. For large glass facades, the robot cleans at a rate of about 4 square meters per minute. The intelligent path planning ensures no spots are missed."
    },
    {
      question: "Do window robots leave streaks?",
      answer: "No, Harpalyk window robots use an intelligent spray system that applies the right amount of cleaning solution, and dual-action microfiber pads that scrub and polish in one pass. The result is streak-free, spotless glass that's cleaner than manual cleaning."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SHINEBOT — Intelligent Window Cleaning Robots | Harpalyk India</title>
        <meta name="description" content="Automatic window cleaning robots with anti-fall AI safety — for apartments, villas & high-rise glass across India. Starting ₹45,999 with free demo & 2-year warranty." />
        <meta name="keywords" content="window cleaning robot india, automatic window cleaner, glass cleaning robot, window washing robot, high rise window cleaner, commercial window robot" />
        <link rel="canonical" href="https://harpalyk.com/window-cleaning-robots" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <div className="h-1 bg-gradient-to-r from-cyan-500 via-cyan-400 to-transparent" data-testid="family-accent-bar-shinebot" aria-hidden="true" />
        <Breadcrumbs items={[{ label: 'Window Cleaning Robots' }]} />
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-cyan-900 via-cyan-800 to-blue-900 text-white py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  HARPALYK · SHINEBOT FAMILY
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  SHINEBOT — Intelligent Window Cleaning Robots
                </h1>
                <p className="text-xl text-cyan-100 mb-8 leading-relaxed">
                  Automatic <strong>window cleaning robots</strong> that safely clean glass surfaces at any height. 
                  Perfect for apartments, offices, hotels, and commercial buildings. Anti-fall technology ensures complete safety.
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a href="#products" className="bg-cyan-500 hover:bg-cyan-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    View Window Robots <ArrowRight className="w-5 h-5" />
                  </a>
                  <a href="tel:+917011370322" className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    <Phone className="w-5 h-5" /> Free Demo
                  </a>
                </div>
                
                <div className="flex flex-wrap gap-6 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">3000Pa</p>
                    <p className="text-sm text-cyan-200">Suction Power</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">30+ Floors</p>
                    <p className="text-sm text-cyan-200">Safe Height</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-cyan-400">₹45,999</p>
                    <p className="text-sm text-cyan-200">Starting Price</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80" 
                  alt="Window Cleaning Robot for High-Rise Buildings" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Technology</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                How Window Cleaning Robots Clean High-Rise Buildings
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Advanced technology makes window cleaning safe, efficient, and streak-free
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Secure Attachment", desc: "Powerful suction motors create a vacuum seal on the glass surface" },
                { step: "2", title: "Smart Navigation", desc: "AI sensors detect edges and plan the most efficient cleaning path" },
                { step: "3", title: "Spray & Clean", desc: "Intelligent spray system applies cleaning solution while pads scrub" },
                { step: "4", title: "Polish Finish", desc: "Dual-action microfiber pads polish the glass streak-free" }
              ].map((item, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-cyan-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Benefits</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Why Choose Robotic Window Cleaners?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="bg-cyan-100 p-4 rounded-xl">
                      <benefit.icon className="w-8 h-8 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product */}
        <section id="products" className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">Our Product</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                ShineBot C2 Window Cleaning Robot
              </h2>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="grid md:grid-cols-2">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/6j2kav5l_Window%20robo.jpeg" 
                    alt="Harpalyk SHINEBOT ShineBot C2 — window cleaning robot" 
                    className="w-full h-80 object-cover"
                  />
                  <BrandChip family="SHINEBOT" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-cyan-600 text-white px-3 py-1 rounded-full text-sm font-medium">Best Seller</span>
                  </div>
                  <p className="text-[11px] font-semibold tracking-[0.18em] text-gray-400 uppercase mb-1">HARPALYK · SHINEBOT</p>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">ShineBot C2</h3>
                  <p className="text-3xl font-bold text-cyan-600 mb-4">₹45,999</p>
                  
                  <ul className="space-y-3 mb-6">
                    {[
                      "3000Pa suction power",
                      "Intelligent water spray system",
                      "Anti-fall protection sensors",
                      "Works on framed & frameless glass",
                      "Backup battery included",
                      "Safety rope + remote control",
                      "2-year warranty"
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <a href="/products/shinebot-c2" className="block text-center bg-cyan-600 hover:bg-cyan-700 text-white py-3 rounded-lg font-semibold transition-all">
                    View ShineBot C2 — Specs & Price
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Clean Windows Without the Risk
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Stop risking safety for clean windows. Our robots handle everything from apartment windows to commercial glass facades.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:+917011370322" className="bg-white text-cyan-700 px-8 py-4 rounded-lg font-bold hover:bg-cyan-100 transition-all flex items-center gap-2">
                <Phone className="w-5 h-5" /> +91-7011370322
              </a>
              <a href="mailto:sales@harpalyk.com" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all flex items-center gap-2">
                <Mail className="w-5 h-5" /> sales@harpalyk.com
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-cyan-600 font-semibold uppercase tracking-wide text-sm">FAQs</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Frequently Asked Questions About Window Cleaning Robots
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-cyan-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default WindowCleaningRobotsPage;
